import { useEffect, useState } from "react";
import { useProjects } from "../lib/ProjectContext";
import { api } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { DocumentsTable } from "../components/DocumentsTable";
import { AttachSourceMenu } from "../components/AttachSourceMenu";
import { DraftTableReviewModal } from "../components/DraftTableReviewModal";
import type { DocumentRecord } from "@shared/types";

export function SourcesPage() {
  const { selectedProjectId } = useProjects();
  const { role, loading: meLoading } = useCurrentAppUser();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reviewDoc, setReviewDoc] = useState<DocumentRecord | null>(null);

  function load() {
    if (!selectedProjectId) return;
    setLoading(true);
    api
      .get<{ documents: DocumentRecord[] }>(`/api/projects/${selectedProjectId}/sources`)
      .then((data) => setDocuments(data.documents))
      .catch((err) => setError(err.message || "Failed to load sources."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [selectedProjectId]);

  async function approve(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/approve`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approval failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function archive(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/archive`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Archive failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function unarchive(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/unarchive`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unarchive failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function del(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/delete`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (meLoading) return <p className="muted">Loading…</p>;
  // Owner-only: the full source library (including the shared statewide
  // registry, upload, and approve/archive/delete) is a management surface,
  // not something every role needs to browse — everyone still gets full
  // Ask answers built from these same sources, and project-specific
  // documents remain visible to all roles on Contract Docs.
  if (role !== "owner") {
    return (
      <div className="card">
        <p>Only owners can manage the source library.</p>
      </div>
    );
  }

  if (loading) return <p className="muted">Loading sources…</p>;

  return (
    <div className="content-col-wide">
      <div className="sources-head">
        <div>
          <p className="sources-desc" style={{ marginTop: 0 }}>
            Only <strong>Approved</strong> documents are used to answer questions. Shared statewide ODOT sources
            appear for every project.
          </p>
        </div>
        {role === "owner" && selectedProjectId && <AttachSourceMenu projectId={selectedProjectId} onUploaded={load} />}
      </div>
      {error && <p className="error-text">{error}</p>}
      <DocumentsTable
        documents={documents}
        role={role}
        busyId={busyId}
        onApprove={approve}
        onArchive={archive}
        onUnarchive={unarchive}
        onDelete={del}
        onReviewTables={setReviewDoc}
        groupByCategory
      />
      {reviewDoc && selectedProjectId && (
        <DraftTableReviewModal
          projectId={selectedProjectId}
          document={reviewDoc}
          onClose={() => setReviewDoc(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
